import { useParams } from 'react-router-dom';
import { Typography, Grid, Chip } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import { makeStyles } from '@material-ui/core/styles';
import {
  InfoCard,
  Header,
  Page,
  Content,
  ContentHeader,
  Progress,
  ResponseErrorPanel,
  Link,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { appExtensionsApiRef, appExtensionPreviewImageApiRef } from '../../api';
import { AppExtensionManifest } from '../../types';
import { placeholderImage } from '../../utils/placeholderImage';

// Matches the fixture placeholder image dimensions — the widest we expect a
// real preview screenshot to need to be shown at.
const PREVIEW_MAX_WIDTH = 480;
const PREVIEW_HEIGHT = 270;

const useStyles = makeStyles(theme => ({
  previewCard: {
    maxWidth: PREVIEW_MAX_WIDTH,
  },
  // Fixed frame so a preview image of ANY real-world size/aspect ratio
  // (authors control this file, not us) can't stretch the card's layout —
  // object-fit: contain shows the whole image letterboxed rather than
  // cropping or distorting it.
  previewImage: {
    width: '100%',
    height: PREVIEW_HEIGHT,
    objectFit: 'contain',
    display: 'block',
    backgroundColor: theme.palette.background.default,
  },
  // width: '1%' is a table-layout:auto trick — combined with whiteSpace:
  // nowrap it makes the browser size this column to its minimum content
  // width and give ALL remaining space to the value column, even though
  // the table itself stays full width (MUI's Table default).
  titleCell: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    width: '1%',
    paddingRight: theme.spacing(4),
    border: 0,
    verticalAlign: 'top',
  },
  contentCell: {
    border: 0,
    verticalAlign: 'top',
  },
}));

const renderMetadataValue = (value: React.ReactNode) => {
  if (typeof value !== 'boolean') {
    return value;
  }
  return value ? '✅' : '❌';
};

const MetadataTable = ({
  metadata,
}: {
  metadata: Record<string, React.ReactNode>;
}) => {
  const classes = useStyles();
  return (
    <Table size="small">
      <TableBody>
        {Object.entries(metadata).map(([title, value]) => (
          <TableRow key={title}>
            <TableCell className={classes.titleCell}>{title}</TableCell>
            <TableCell className={classes.contentCell}>
              {renderMetadataValue(value)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const ChipList = ({
  values,
  emptyLabel = 'None',
}: {
  values?: string[];
  emptyLabel?: string;
}) => {
  if (!values || values.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <>
      {values.map(value => (
        <Chip key={value} label={value} size="small" />
      ))}
    </>
  );
};

export const AppExtensionManifestContent = ({
  manifest,
}: {
  manifest: AppExtensionManifest;
}) => {
  const classes = useStyles();
  const previewImageApi = useApi(appExtensionPreviewImageApiRef);

  // A separate fetch since the manifest no longer carries resolved preview
  // image bytes/URLs itself — this is now an on-demand GitHub call, made
  // only for the one extension being viewed (not the whole list).
  const { value: previewImageSrc } = useAsync(async () => {
    if (!manifest?.previewImageUrl) {
      return undefined;
    }
    return previewImageApi.getPreviewImageDataUrl({
      sourceRepository: manifest.sourceRepository,
      previewImageUrl: manifest.previewImageUrl,
    });
  }, [previewImageApi, manifest]);

  return (
    <Grid container spacing={3} direction="column">
      <Grid item container spacing={3} alignItems="stretch">
        <Grid item xs={12} md>
          <InfoCard title="Identity" variant="gridItem">
            <MetadataTable
              metadata={{
                Description: manifest.description ?? 'No description available',
                'Extension ID': manifest.extensionId,
                Type: manifest.type,
                'Framework version': manifest.frameworkVersion,
                Source: (
                  <Link
                    to={`https://github.com/${manifest.sourceRepository}/blob/main/${manifest.sourcePath}`}
                    externalLinkIcon
                  >
                    {manifest.sourceRepository}/{manifest.sourcePath}
                  </Link>
                ),
                Hooks: <ChipList values={manifest.hooks} />,
              }}
            />
          </InfoCard>
        </Grid>
        <Grid item xs={12} md="auto">
          <InfoCard
            title="Preview"
            variant="gridItem"
            className={classes.previewCard}
          >
            <img
              src={previewImageSrc ?? placeholderImage('No preview available')}
              alt={
                previewImageSrc
                  ? `Preview of ${manifest.displayName}`
                  : 'No preview available'
              }
              className={classes.previewImage}
            />
          </InfoCard>
        </Grid>
      </Grid>
      <Grid item>
        <InfoCard title="Guard chain">
          <MetadataTable
            metadata={{
              Hosts: (
                <ChipList
                  values={manifest.supportedHosts}
                  emptyLabel="All hosts supported"
                />
              ),
              Products: (
                <ChipList
                  values={manifest.supportedProducts}
                  emptyLabel="All products supported"
                />
              ),
              Regions: (
                <ChipList
                  values={manifest.supportedRegions}
                  emptyLabel="All regions supported"
                />
              ),
              Scopes: (
                <ChipList
                  values={manifest.requiredScopes}
                  emptyLabel="No scopes required"
                />
              ),
              'Feature flags': (
                <ChipList
                  values={manifest.featureFlagKeys}
                  emptyLabel="Not gated by a feature flag"
                />
              ),
              'Patient context': manifest.requiresPatientContext,
              ...(manifest.trial && { 'Trial type': manifest.trial.type }),
            }}
          />
        </InfoCard>
      </Grid>
      <Grid item>
        <InfoCard title="Placement">
          <MetadataTable
            metadata={{
              Placements: (
                <ChipList
                  values={manifest.placements}
                  emptyLabel="All placements supported"
                />
              ),
            }}
          />
        </InfoCard>
      </Grid>
      <Grid item>
        <InfoCard title="Data events">
          <MetadataTable
            metadata={{
              Emits: (
                <ChipList
                  values={manifest.emits}
                  emptyLabel="Does not emit any events"
                />
              ),
              Consumes: (
                <ChipList
                  values={manifest.consumes}
                  emptyLabel="Does not consume any events"
                />
              ),
              'Initial prop keys': (
                <ChipList
                  values={manifest.initialPropKeys}
                  emptyLabel="No initial props expected"
                />
              ),
            }}
          />
        </InfoCard>
      </Grid>
      {manifest.type === 'plugin' && (
        <Grid item>
          <InfoCard title="Plugin config">
            <MetadataTable
              metadata={{ 'Column span': manifest.columnSpan ?? 1 }}
            />
          </InfoCard>
        </Grid>
      )}
      {manifest.type === 'app-launch' && (
        <Grid item>
          <InfoCard title="App launch config">
            <MetadataTable
              metadata={{
                'Application ID': manifest.applicationId,
                'Supports comms': manifest.supportsComms ?? false,
                'Allows microphone': manifest.allowsMicrophone ?? false,
                'Data events': (
                  <ChipList
                    values={manifest.dataEvents}
                    emptyLabel="No data events declared"
                  />
                ),
              }}
            />
          </InfoCard>
        </Grid>
      )}
    </Grid>
  );
};

export const AppExtensionDetail = () => {
  const { extensionId } = useParams<{ extensionId: string }>();
  const appExtensionsApi = useApi(appExtensionsApiRef);

  const {
    value: manifest,
    loading,
    error,
  } = useAsync(async () => {
    if (!extensionId) {
      return undefined;
    }
    return appExtensionsApi.getAppExtension(extensionId);
  }, [appExtensionsApi, extensionId]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  if (!manifest) {
    return (
      <Page themeId="tool">
        <Header title="App Extension not found" />
        <Content>
          <Typography variant="body1">
            No app extension with ID "{extensionId}" was found.
          </Typography>
          <Link to="/app-extensions">Back to App Extensions</Link>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title={manifest.displayName} subtitle={manifest.extensionId} />
      <Content>
        <ContentHeader title="Manifest">
          <Link to="/app-extensions">Back to App Extensions</Link>
        </ContentHeader>
        <AppExtensionManifestContent manifest={manifest} />
      </Content>
    </Page>
  );
};
